import { useGreeting } from '../../ui/greeting';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  overviewTodo,
  overviewTodoDone,
  type OverviewTodo,
  type TodoDone,
  api,
  flagsFromMeasure,
  fmtAgeMinutes,
  fmtAgeSince,
  fmtClock,
  fmtCount,
  fmtMoneyCompact,
  fmtPct,
  type BuildingOverview,
  type CaptureSummary,
  type FacilityChecklist,
  type FigureCell,
  type LinkKey,
  type LinkState,
  type OverviewCensus,
  type OverviewQueues,
  type OverviewTriage,
  type OverviewWorklistResponse,
  type PulseV3Facilities,
  type ApiError,
  type OccupancyReference,
} from '@aithora/shared';
import { ErrorNote } from '../../ErrorNote';
import { useSession } from '../../session/SessionContext';
import { useDemoBuilding, useTagSuffix } from '../../session/DemoScope';
import { useNavBadges } from '../../shell/Shell';
import { roleChipText } from '../../shell/TopBar';
import { CHECKLIST_PREFS_EVENT } from '../../shell/FirstTimeBanner';
import { useReadOnly } from '../../ui/ReadOnly';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import type { QueryState } from '../../useQuery';
import { HonestyBadge } from '../../ui/HonestyBadge';
import { EmptyState, QueryBlock, Skeleton } from '../../ui/states';
import { initials, useApi } from './common';
import { NeedsTiles, type Tile } from './operatorBlocks';
import { tilesFromBadges } from './badgeTiles';
import { AdminTriage, unplacedCount } from './AdminTriage';
import { BuildingTodo, todoCount } from './TodoTab';
import { useBuildLabels } from '../../ui/buildLabels';
import { useReferenceBenchmarks } from '../../referenceBenchmarks';
import { FamilyCheckinsBlock, NeedsYouToday, TeamMemoCard, pilotPill } from './v9Blocks';

// A port of the Console V3 design's Facility Admin Overview (fs*, pfFa* and fa* in
// "Aithora ConsoleV3.dc.html"). Class names map to its inline styles in facilityAdmin.css.
// Most blocks read GET /overview/building (v3/overview/building.ts); while it loads or
// if it fails, each block falls back to the routes that exist, or says what it is
// waiting on.

// ── Setup checklist (design: fs*) ───────────────────────────────────────────

/** Where each checklist step is done (design: the ITEMS' onAction views). */
const ITEM_TO: Record<string, string> = { residents: '/residents' };
const itemTo = (key: string) => ITEM_TO[key] ?? '/settings';
/** The button a done step keeps (design ITEMS: Review, Open, Invite, View). */
const DONE_LABEL: Record<string, string> = {
  confirm_building: 'Review', baa: 'Open', lookup: 'Answer', invite_team: 'Invite', residents: 'Open', test_alert: 'Send test', ehr: 'View',
};

type Step = FacilityChecklist['items'][number];
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : null);

/** What a step shows beside its title, in the design's words, from what the server sent. Null when nothing fits. */
export function stepEvidence(i: Step): string | null {
  const d = i.detail ?? {};
  switch (i.key) {
    case 'confirm_building':
      return i.done ? 'CMS profile · confirmed' : 'pre-filled from the CMS record';
    case 'baa': {
      if (i.done) return `signed${day(i.doneAt) ? ` ${day(i.doneAt)}` : ''} · countersigned`;
      const s = typeof d.status === 'string' ? d.status : 'none';
      return s === 'signed' ? 'signed · waiting for Aithora to countersign' : s === 'sent' ? 'sent to you to sign' : 'not signed yet';
    }
    case 'lookup': {
      const own = typeof d.ownership === 'string' ? d.ownership : null;
      const units = num(d.units);
      return own && units != null ? `${own} · ${units} units` : 'ownership and units not set';
    }
    case 'invite_team': {
      const sent = num(d.sent) ?? 0;
      const ok = num(d.accepted) ?? 0;
      if (!sent) return 'nobody invited yet';
      return `${ok} of ${sent} accepted${sent > ok ? ` · ${sent - ok} pending` : ''}`;
    }
    case 'residents': {
      const n = num(d.residents) ?? 0;
      // "Loaded" counts every resident record, discharged ones too; say how many are in the
      // building so it never reads against the "N residents in the building" headline.
      const inHouse = num(d.inHouse);
      if (!n) return 'no residents yet';
      return `${n} resident${n === 1 ? '' : 's'} loaded` + (inHouse != null && inHouse !== n ? ` · ${inHouse} in the building` : '');
    }
    case 'test_alert': {
      const s = num(d.ackSeconds);
      return i.done ? `acknowledged by the on-call nurse${s != null ? ` in ${s}s` : ''}` : 'never tested';
    }
    case 'ehr':
      return i.done ? 'connected' : 'Aithora is connecting it. Nothing needed from you.';
    default:
      return null;
  }
}

/** Aithora's own steps, as the finish line names them. */
const OURS_NOUN: Record<string, string> = { ehr: 'EHR connection', devices: 'device shipment', baa_countersign: 'BAA countersignature' };

export function SetupChecklist({ q, onPrefs, onTested }: { q: QueryState<FacilityChecklist>; onPrefs: (p: { dismissed?: boolean; collapsed?: boolean }) => Promise<void>; onTested?: () => void }) {
  const { client } = useSession();
  const readOnly = useReadOnly();
  const [busy, setBusy] = useState(false);
  // A save that failed says so here (the crawl found it thrown as an unhandled error).
  const [prefsErr, setPrefsErr] = useState<ApiError | null>(null);
  // Once your part is done the list folds away (design: fsOpen defaults to !complete).
  const [showAll, setShowAll] = useState(false);
  // "Send test": rings the nurse on call (POST /escalation/test, confirm-first), then
  // reads the test until they acknowledge it; the checklist and the card then read proven.
  const [asking, setAsking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [ringing, setRinging] = useState<string | null>(null);
  // What the test page actually did: who it rang, and why nothing went out when nothing did.
  const [sentTo, setSentTo] = useState<{ self: boolean; name: string | null; live: boolean; reason: string | null } | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!ringing || tick > 120) return;
    const t = setTimeout(async () => {
      try {
        const st = await api.testStatus(client, { params: { id: ringing } });
        if (st.ackSeconds !== null) {
          setRinging(null);
          onTested?.();
          return;
        }
      } catch {
        /* keep ringing; the next read may answer */
      }
      setTick((n) => n + 1);
    }, 1000);
    return () => clearTimeout(t);
  }, [ringing, tick, client, onTested]);
  if (q.loading && !q.data) return null;
  if (q.error && !q.data) {
    return (
      <div className="fs" data-testid="setup-checklist">
        <QueryBlock q={q}>{() => null}</QueryBlock>
      </div>
    );
  }
  const d = q.data;
  if (!d || d.dismissed) return null;
  const yours = d.items.filter((i) => i.owner === 'facility');
  const ours = d.items.filter((i) => i.owner === 'aithora');
  const complete = d.yoursComplete;
  const pill = pilotPill(d.pilot);
  const open = complete ? showAll : !d.collapsed;
  const pct = d.progress.total ? Math.round((d.progress.done / d.progress.total) * 100) : 0;
  const set = async (p: { dismissed?: boolean; collapsed?: boolean }) => {
    setBusy(true);
    setPrefsErr(null);
    try {
      await onPrefs(p);
    } catch (e) {
      setPrefsErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };
  const isTest = (key: string) => key === 'test_alert';
  const action = (key: string, label: string, cls: string, done = false) =>
    isTest(key) ? (
      <button type="button" className={cls} disabled={readOnly || !!ringing} onClick={() => setAsking(true)}>
        {ringing ? 'Sent' : label}
      </button>
    ) : key === 'confirm_building' && !done ? (
      <button type="button" className={cls} disabled={readOnly} onClick={() => setConfirming(true)}>
        {label}
      </button>
    ) : (
      <Link to={itemTo(key)} className={cls}>
        {label}
      </Link>
    );
  const ringingLine = () =>
    !sentTo ? 'ringing the on-call nurse…'
      : !sentTo.live ? (sentTo.reason ?? 'Nothing went out.')
        : sentTo.self ? 'nobody is on call, so it rang you: tap I got it'
          : `ringing ${sentTo.name ?? 'the on-call nurse'}…`;
  const evidence = (i: FacilityChecklist['items'][number]) =>
    isTest(i.key) && !i.done && (ringing || (sentTo && !sentTo.live)) ? ringingLine()
      : i.evidence ?? stepEvidence(i) ?? (i.done ? (i.doneAt ? `done ${fmtAgeSince(i.doneAt)} ago` : 'done') : i.estimateMinutes ? `${i.estimateMinutes} min` : '');
  // V6 keeps a button on every row: a done step opens what it did (Review, Open, Invite).
  const label = (i: FacilityChecklist['items'][number]) => i.actionLabel ?? (i.done ? DONE_LABEL[i.key] ?? null : null);
  const numClass = (i: FacilityChecklist['items'][number]) => `fs__num${i.done ? ' fs__num--done' : d.next && i.key === d.next.key ? ' fs__num--next' : ''}`;
  return (
    <section className="fs" data-testid="setup-checklist" aria-label="Setup checklist">
      {prefsErr && <ErrorNote error={prefsErr} compact />}
      <div className="fs__head">
        <h2 className="fs__title">{complete ? 'Your facility is set up' : 'Set up your facility'}</h2>
        <span className="fs__progress">{`${d.progress.done} of ${d.progress.total} yours done`}</span>
        {/* empty once complete, but kept: the design's line keeps the gap */}
        <span className="fs__time" title={complete ? undefined : `${d.progress.minutesBasis.toUpperCase()}: an estimate from the steps left`}>
          {complete ? '' : `about ${d.progress.minutesLeft} min left`}
        </span>
        {pill && (
          <span className="fs__pilot" data-testid="pilot-pill" title={pill.tip}>
            {pill.text}
          </span>
        )}
        <span className="spacer" />
        <span className="fs__links">
          <Link to="/guide" className="fs__guide">
            Guide
          </Link>
          <button type="button" className="fs__toggle" disabled={busy} onClick={() => (complete ? setShowAll(!showAll) : set({ collapsed: open }))}>
            {open ? 'Collapse' : `Show all ${d.items.length}`}
          </button>
          <button type="button" className="fs__dismiss" disabled={busy} onClick={() => set({ dismissed: true })}>
            {complete ? 'Dismiss' : 'Hide for now'}
          </button>
        </span>
      </div>
      <div className={`fs__bar${complete ? ' fs__bar--done' : ''}`} aria-hidden="true">
        <i style={{ width: `${pct}%` }} />
      </div>
      {d.next && (
        <div className="fs__next">
          <span className="fs__nextk">NEXT</span>
          <span className="fs__nextt">{d.next.title}</span>
          <span className="fs__why">{d.next.why}</span>
          {d.next.estimateMinutes !== null && <span className="fs__min">{`~${d.next.estimateMinutes} min`}</span>}
          {action(d.next.key, yours.find((i) => i.key === d.next!.key)?.actionLabel ?? 'Start', 'fs__act')}
        </div>
      )}
      {complete && (
        <div className="fs__done">
          <span className="fs__donet">Your part is done</span>
          <span className="fs__donew">
            {[
              `Test alert acknowledged in ${d.finish?.testAckSeconds ?? '–'}s.`,
              ...ours.filter((i) => !i.done).map((i) => `Aithora is finishing the ${OURS_NOUN[i.key] ?? i.title.toLowerCase()}.`),
              'Your team gets their own short tour when they first sign in.',
            ].join(' ')}
          </span>
          <Link to="/settings" className="fs__doneact">
            See who has not signed in
          </Link>
        </div>
      )}
      {open && (
        <div className="fs__cols">
          <div className="fs__col">
            <span className="fs__group">YOURS</span>
            <div className="fs__items">
              {yours.map((i, n) => (
                <div key={i.key} className="fs__item" title={i.why}>
                  <span className={numClass(i)}>{i.done ? '✓' : String(n + 1)}</span>
                  <span className="fs__text">
                    <span className={`fs__t${i.done ? ' fs__t--done' : ''}`}>{i.title}</span>
                    <span className={`fs__ev${i.done ? ' fs__ev--done' : ''}`} title={evidence(i) || undefined}>{evidence(i)}</span>
                  </span>
                  {label(i) && action(i.key, label(i)!, 'fs__btn', i.done)}
                  {isTest(i.key) && ringing && sentTo?.self && sentTo.live && (
                    <button
                      type="button"
                      className="fs__btn"
                      disabled={readOnly}
                      onClick={async () => {
                        await api.testAck(client, { params: { id: ringing }, body: {} });
                        setTick((n) => n + 1);
                      }}
                    >
                      I got it
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="fs__col">
            <span className="fs__group">AITHORA'S</span>
            {ours.map((i) => (
              <div key={i.key} className="fs__item" title={i.why}>
                <span className={numClass(i)}>{i.done ? '✓' : '•'}</span>
                <span className="fs__text fs__text--stack">
                  <span className="fs__t">{i.title}</span>
                  <span className={`fs__ev${i.done ? ' fs__ev--done' : ''}`} title={evidence(i) || undefined}>{evidence(i)}</span>
                </span>
                {(i.actionLabel ?? DONE_LABEL[i.key]) && (
                  <Link to="/settings" className="fs__btn">
                    {i.actionLabel ?? DONE_LABEL[i.key]}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {asking && d.facility && (
        <ConfirmDialog
          title="Send a test alert?"
          body="It rings the on-call nurse's phone as a TEST. It never involves a resident or 911. They tap acknowledge, and you see how fast the path really is."
          confirmLabel="Send test"
          onConfirm={async () => {
            const r = await api.escalationTest(client, { body: withConfirm({}) });
            setSentTo({ self: r.target.source === 'self_no_on_call', name: r.target.name, live: r.live, reason: r.reason });
            setTick(0);
            // Nothing went out: there is nothing to wait for.
            setRinging(r.live ? r.testId : null);
            if (!r.live) onTested?.();
          }}
          onClose={() => setAsking(false)}
        />
      )}
      {confirming && d.facility && (
        <ConfirmDialog
          title="Is this your building?"
          body={`${d.facility.name}, as the CMS catalog lists it. Confirm it is right, or ask Aithora to correct it before you load residents.`}
          confirmLabel="Yes, this is us"
          onConfirm={async () => {
            await api.facilityConfirmProfile(client, { params: { id: d.facility!.id }, body: {} });
            onTested?.();
          }}
          onClose={() => setConfirming(false)}
        />
      )}
    </section>
  );
}

// ── Is Aithora working here (design: pfFa*) ─────────────────────────────────

const LINK_LABEL: Record<LinkKey, string> = {
  sensors: 'Sensors',
  companion: 'Atlas',
  ehr_feed: 'EHR feed',
  nurse_call: 'Nurse call',
  escalation_path: 'Escalation path',
  staff_reachable: 'Staff reachable',
};
const LINK_ORDER: LinkKey[] = ['sensors', 'companion', 'ehr_feed', 'nurse_call', 'escalation_path', 'staff_reachable'];
/** The design's PF_C: ink and tint per state. */
const LINK_TONE: Record<LinkState, [string, string]> = {
  down: ['#B3261E', 'rgba(179,38,30,.08)'],
  degraded: ['#8A5A00', 'rgba(184,134,11,.10)'],
  unknown: ['#54606C', 'rgba(34,39,46,.06)'],
  ok: ['#1F6B52', 'rgba(47,138,111,.10)'],
  na: ['#6B7480', 'rgba(34,39,46,.04)'],
};

function worstLink(states: LinkState[]): LinkState {
  for (const s of ['down', 'degraded', 'unknown'] as const) if (states.includes(s)) return s;
  return 'ok';
}

export function IsItWorking({ q }: { q: QueryState<PulseV3Facilities> }) {
  const { client } = useSession();
  const readOnly = useReadOnly();
  const [asked, setAsked] = useState<'test' | 'report' | null>(null);
  const b = q.data?.facilities[0];
  if (!b) return null;
  const keys = (b.link_order.length ? (b.link_order as LinkKey[]) : LINK_ORDER).filter((k) => b.links[k]);
  const w = worstLink(keys.map((k) => b.links[k].state));
  // Proven: a test alert reached a person on shift and was acknowledged (design: fsTest.ack).
  const ack = b.links.escalation_path?.state === 'ok' ? (b.last_proven?.ack_seconds ?? null) : null;
  // Every link that is not working counts (not connected included), so the sentence never says
  // "one gap" above a row showing four; the escalation path, when unproven, is named the most urgent.
  const gaps = keys.filter((k) => b.links[k].state !== 'ok');
  const unproven = gaps.includes('escalation_path') && b.links.escalation_path.state !== 'na';
  const verdict =
    w === 'ok'
      ? ack !== null
        ? `Yes. Your test alert reached the on-call nurse and was acknowledged in ${ack}s, so the escalation path is proven here.`
        : 'Yes. Every connected link is working.'
      : w === 'unknown'
        ? gaps.length <= 1
          ? unproven || !gaps.length
            ? 'Yes, with one gap: the escalation path has never been proven here. Until a test alert reaches a person on shift and is acknowledged, we cannot promise a page would reach anyone.'
            : `Yes, with one gap: ${LINK_LABEL[gaps[0]]}.`
          : `Yes, with ${gaps.length} gaps: ${gaps.map((k) => LINK_LABEL[k]).join(', ')}.`
        : 'Not fully. Aithora has been alerted and will contact you.';
  const verdictMore =
    w === 'unknown' && gaps.length > 1
      ? unproven
        ? 'The most urgent: the escalation path has never been proven here. Until a test alert reaches a person on shift and is acknowledged, we cannot promise a page would reach anyone.'
        : `The most urgent: ${LINK_LABEL[gaps[0]]}.`
      : '';
  const note =
    asked === 'test'
      ? 'Aithora will agree a time with you. The test alert goes to your charge nurse only, never to residents.'
      : asked === 'report'
        ? 'The Aithora on-call has it and will contact you.'
        : b.links.nurse_call?.state === 'na'
          ? 'Nurse call reads not connected: your call lights run outside Aithora, so keep testing them the way you do today.'
          : '';
  // Book a chain test and Report a problem reach Aithora (PROPOSED routes).
  const ask = async (what: 'test' | 'report') => {
    if (what === 'test') await api.chainTestRequest(client, { params: { id: b.organization_id }, body: {} });
    else await api.problemReport(client, { params: { id: b.organization_id }, body: {} });
    setAsked(what);
  };
  return (
    <section className="wk" aria-label="Is Aithora working">
      <div className="wk__head">
        <span className="wk__dot" style={{ background: LINK_TONE[w][0] }} aria-hidden="true" />
        <h2 className="wk__title">{`Is Aithora working at ${b.name}?`}</h2>
        <HonestyBadge flags={[{ real: q.data?.real }]} figure="Is Aithora working" className="ov-badge-mini" hide={['MEASURED']} />
      </div>
      <div className="wk__verdict">
        <span>{verdict}</span>
        {verdictMore && <> <span>{verdictMore}</span></>}
      </div>
      <div className="wk__links">
        {keys.map((k, i) => {
          const l = b.links[k];
          const [ink, tint] = LINK_TONE[l.state];
          const proven = k === 'escalation_path' && ack !== null;
          return (
            <span key={k} className="wk__link" style={{ background: l.state === 'ok' || l.state === 'na' ? 'transparent' : tint }}>
              <span className="wk__lrow">
                <span className={`wk__ldot${l.state === 'na' ? ' wk__ldot--na' : ''}`} style={l.state === 'na' ? undefined : { background: ink }} />
                <span className="wk__lname">{`${i + 1} ${LINK_LABEL[k]}`}</span>
              </span>
              <span className="wk__lval" style={proven ? { color: '#1F6B52', fontWeight: 700 } : { color: l.state === 'ok' ? '#3C454F' : ink, fontWeight: l.state === 'ok' || l.state === 'na' ? 500 : 700 }}>
                {proven ? `proven · acked in ${ack}s` : (l.value ?? (l.state === 'na' ? 'not connected' : l.state))}
              </span>
            </span>
          );
        })}
      </div>
      <div className="wk__acts">
        <button type="button" className="wk__test" disabled={readOnly || asked === 'test'} onClick={() => ask('test')}>
          {ack !== null ? 'Test again' : asked === 'test' ? 'Test requested' : 'Book a chain test'}
        </button>
        <button type="button" className="wk__report" disabled={readOnly || asked === 'report'} onClick={() => ask('report')}>
          {asked === 'report' ? 'Sent to Aithora' : 'Report a problem'}
        </button>
        {note && <span className="wk__note">{note}</span>}
      </div>
    </section>
  );
}

// ── Building blocks ─────────────────────────────────────────────────────────

/**
 * " · SAMPLE" after a block's meta when its figures are sample data; nothing inside a
 * demo building scope, whose banner at the top of the page already says so.
 */
function useSampleTail(): (real: boolean | undefined) => string {
  const suffix = useTagSuffix();
  return (real) => (real === false ? suffix('SAMPLE') : '');
}

/** "2d 3h", "1d 6h", "4h", "52m" (the design's family ages). */
function dayHours(iso: string, now: Date): string {
  const m = Math.max(0, Math.floor((now.getTime() - Date.parse(iso)) / 60000));
  const h = Math.floor(m / 60);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

/** "18m", "1h 06m" (the design's escalation ages). */
function hourMinutes(iso: string, now: Date): string {
  const m = Math.max(0, Math.floor((now.getTime() - Date.parse(iso)) / 60000));
  const h = Math.floor(m / 60);
  return h ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
}

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2);

function Head({ title, meta, next, children }: { title: string; meta?: ReactNode; next?: boolean; children?: ReactNode }) {
  return (
    <div className={`fa-h${next ? ' fa-h--next' : ''}`}>
      <h3 className="fa-h__t">{title}</h3>
      {meta !== undefined && <span className="fa-h__m">{meta}</span>}
      {children}
    </div>
  );
}

function SmallFigures({ cells }: { cells: FigureCell[] }) {
  return (
    <span className="faq">
      {cells.map((c) => (
        <Link key={c.k} to={c.to ?? '/residents'} className="faq__cell" title={c.d ? `${c.k}: ${c.d}` : c.k}>
          <span className={`faq__v fa-tone--${c.tone ?? 'none'}`}>{c.v}</span> <span className="faq__k">{c.k}</span>
        </Link>
      ))}
    </span>
  );
}

function Hero({ b, census }: { b: BuildingOverview | null; census: QueryState<OverviewCensus> }) {
  if (b) {
    return (
      <div className="fah">
        {b.hero.map((h) => (
          <Link key={h.k} to={h.to ?? '/residents'} title={h.title ?? h.k} className="fah__fig">
            <span className="fah__top">
              <span className={`fah__v fa-tone--${h.tone ?? 'none'}`}>{h.v}</span>
              <span className="fah__k">{h.k}</span>
            </span>
            <span className="fah__d">{h.d}</span>
          </Link>
        ))}
      </div>
    );
  }
  // Until the building route exists: the census route's figures.
  const cs = census.data && census.data.scoped ? census.data : null;
  const figs: FigureCell[] = cs
    ? [
        { v: cs.current.value === null ? '–' : fmtCount(cs.current.value), k: 'residents', d: cs.beds.value === null ? '' : `of ${fmtCount(cs.beds.value)} beds`, to: '/residents' },
        { v: cs.occupancy.value === null ? '–' : fmtPct(cs.occupancy.value * (cs.occupancy.value <= 1 ? 100 : 1), 0), k: 'occupancy', d: cs.occupancy.basis, to: '/performance' },
        { v: cs.bedsOpen.value === null ? '–' : fmtCount(cs.bedsOpen.value), k: 'beds open', d: cs.bedsOpen.basis, to: '/residents' },
        { v: cs.admissions.value === null ? '–' : fmtCount(cs.admissions.value), k: 'admissions', d: `last ${cs.window.days} days`, to: '/residents' },
        { v: cs.discharges.value === null ? '–' : fmtCount(cs.discharges.value), k: 'discharges', d: `last ${cs.window.days} days`, to: '/residents' },
      ]
    : [];
  return (
    <div className="fah">
      {census.error && !census.data ? (
        <QueryBlock q={census}>{() => null}</QueryBlock>
      ) : census.data && !census.data.scoped ? (
        <EmptyState compact title="Census is per building" body={census.data.reason} />
      ) : (
        figs.map((h) => (
          <Link key={h.k} to={h.to ?? '/residents'} title={h.d} className="fah__fig">
            <span className="fah__top">
              <span className="fah__v">{h.v}</span>
              <span className="fah__k">{h.k}</span>
            </span>
            <span className="fah__d">{h.d}</span>
          </Link>
        ))
      )}
    </div>
  );
}

function tileLoud(t: Tile, all: Tile[]): string {
  if (t.sev !== 'breach' || t.n <= 0) return `ov-tile ov-tile--${t.sev}`;
  const vals = all.filter((x) => x.sev === 'breach').map((x) => x.n);
  const hi = Math.max(...vals);
  const lo = Math.min(...vals);
  const f = hi === lo ? 0.6 : 0.35 + ((t.n - lo) / (hi - lo)) * 0.55;
  return `ov-tile ov-tile--breach${f > 0.5 ? ' ov-tile--loud' : ''}`;
}

export function BuildingNeeds({ b, onTriage }: { b: BuildingOverview; onTriage?: () => void }) {
  const order = { breach: 0, risk: 1, normal: 2 } as const;
  const tiles: Tile[] = b.needs.map((n) => ({ key: n.key, short: n.short, long: n.long, n: n.n, sev: n.sev, to: n.to }));
  const sorted = [...tiles].sort((x, y) => order[x.sev] - order[y.sev]);
  return (
    <section className="fan" aria-label="Needs action">
      <div className="fan__head">
        <h3 className="fan__title">Needs action</h3>
        <span className="fan__sub">Your building today</span>
      </div>
      <span className="fan__tiles">
        {sorted.map((t) => {
          const inner = (
            <>
              <span className="ov-tile__n">{fmtCount(t.n)}</span>
              <span className="ov-tile__k">{t.short}</span>
            </>
          );
          // Nothing to act on is a figure, not a button.
          if (!t.n) {
            return (
              <span key={t.key} title={t.long} className={tileLoud(t, sorted)} data-testid={`tile-${t.key}`} data-static="true">
                {inner}
              </span>
            );
          }
          // Flags not placed live on this page's Triage tab: open it.
          if (t.key === 'flags' && onTriage) {
            return (
              <button key={t.key} type="button" title={t.long} className={tileLoud(t, sorted)} data-testid={`tile-${t.key}`} onClick={onTriage} style={{ font: 'inherit', textAlign: 'inherit', cursor: 'pointer' }}>
                {inner}
              </button>
            );
          }
          return (
            <Link key={t.key} to={t.to} title={t.long} className={tileLoud(t, sorted)} data-testid={`tile-${t.key}`}>
              {inner}
            </Link>
          );
        })}
      </span>
    </section>
  );
}

function Missing({ what }: { what: string }) {
  return <EmptyState compact title="Not served yet" waitingOn={what} />;
}

const BUILDING_ROUTE = 'GET /overview/building';

function AttentionCol({ b, worklist, now }: { b: BuildingOverview | null; worklist: QueryState<OverviewWorklistResponse>; now: Date }) {
  if (b) {
    const rows = b.attention.rows;
    const count = (k: string) => rows.filter((r) => r.kind === k).length;
    const meta = [
      count('deteriorating') && `${count('deteriorating')} deteriorating`,
      count('admit') && `${count('admit')} admit`,
      count('meds') && `${count('meds')} meds`,
      count('task') && `${count('task')} overdue`,
    ]
      .filter(Boolean)
      .join(' · ');
    const KIND = { deteriorating: 'DETERIORATING', admit: 'NEW ADMIT', meds: 'MED PASS', task: 'OVERDUE' } as const;
    return (
      <div className="fa-col">
        <Head title="Needs attention now" meta={meta} />
        {rows.map((r, i) => (
          <Link key={`${r.kind}:${r.residentId}:${i}`} to={r.to} className={`far far--${r.kind}`}>
            <span className="far__ini">{initialsOf(r.name)}</span>
            <span className="far__main">
              <span className="far__l1">
                <span className="far__name">
                  {r.name} <span className="far__room">{r.room ? `Rm ${r.room}` : ''}</span>
                </span>
                <span className={`far__kind far__kind--${r.kind}`}>{KIND[r.kind]}</span>
              </span>
              <span className="far__why">{`${r.why}${r.unit ? ` · ${r.unit}` : ''}`}</span>
            </span>
            <span className="far__verb">{r.verb}</span>
          </Link>
        ))}
        <Link to="/residents" className="fa-more">
          {`All ${fmtCount(b.attention.total)} residents →`}
        </Link>
      </div>
    );
  }
  return (
    <div className="fa-col">
      <Head title="Needs attention now" meta={worklist.data ? `${worklist.data.count} open · severity first` : undefined} />
      <QueryBlock q={worklist} rows={4} isEmpty={(d) => d.items.length === 0} empty={<EmptyState compact title="Nobody needs attention right now" />}>
        {(d) =>
          d.items.slice(0, 6).map((i) => (
            <Link key={`${i.kind}:${i.id}`} to={i.residentId ? '/residents' : '/worklist'} className={`far far--${i.severity === 'high' ? 'deteriorating' : 'task'}`}>
              <span className="far__ini">{initialsOf(i.residentName ?? '?')}</span>
              <span className="far__main">
                <span className="far__name">{i.residentName ?? 'Unassigned'}</span>
                <span className="far__why">{`${i.type.replace(/_/g, ' ').toLowerCase()} · ${i.kind === 'alert' ? 'alert' : 'care request'}`}</span>
              </span>
              <span className="far__age">{fmtAgeSince(i.createdAt, now)}</span>
            </Link>
          ))
        }
      </QueryBlock>
      <Link to="/residents" className="fa-more">
        {'All residents →'}
      </Link>
    </div>
  );
}

function WaitCount({ q, which, title, next }: { q: QueryState<OverviewQueues>; which: 'families' | 'nurse'; title: string; next?: boolean }) {
  const w = q.data && q.data.scoped ? q.data[which] : null;
  return (
    <>
      <Head title={title} next={next} meta={w ? (w.waiting.value ? `${fmtCount(w.waiting.value)} waiting · oldest ${fmtAgeMinutes(w.oldestMinutes.value)}` : 'nobody waiting') : undefined}>
        {w && <HonestyBadge flags={[flagsFromMeasure(w.waiting)]} figure={title} className="ov-badge-mini" hide={['MEASURED']} />}
      </Head>
      <QueryBlock q={q} rows={1} isEmpty={(d) => !d.scoped} empty={<EmptyState compact title="Not scoped to a building" />}>
        {() => <Missing what={`the ${which === 'families' ? 'family threads' : 'nurse queue'} rows (${BUILDING_ROUTE})`} />}
      </QueryBlock>
    </>
  );
}

function EscalationsAndFamilies({ b, queues, now }: { b: BuildingOverview | null; queues: QueryState<OverviewQueues>; now: Date }) {
  const sampleTail = useSampleTail();
  if (!b) {
    return (
      <div className="fa-col">
        <Head title="Crisis escalations" />
        <Missing what={`a building-scoped escalation list with claim state (${BUILDING_ROUTE})`} />
        <WaitCount q={queues} which="families" title="Families waiting" next />
      </div>
    );
  }
  const esc = b.escalations.rows;
  const unclaimed = esc.filter((e) => !e.claimedBy).length;
  const fam = b.families.rows;
  const oldest = fam.length ? fam.reduce((a, r) => (Date.parse(r.waitingSince) < Date.parse(a.waitingSince) ? r : a)) : null;
  return (
    <div className="fa-col">
      <Head title="Crisis escalations" meta={`${esc.length} open · ${unclaimed} unclaimed`} />
      {esc.map((e) => (
        <Link key={e.id} to="/guardian" className={`far far--esc${e.claimedBy ? ' far--claimed' : ''}`}>
          <span className="far__main">
            <span className="far__name">
              {e.name} <span className="far__room">{e.room ? `Rm ${e.room}` : ''}</span>
            </span>
            <span className="far__why far__why--muted">{e.what}</span>
          </span>
          <span className="far__age far__age--big">{hourMinutes(e.openedAt, now)}</span>
          {e.claimedBy ? (
            <span className="far__who" title={`Claimed by ${e.claimedBy}`}>
              {initials(e.claimedBy)}
            </span>
          ) : (
            <span className="far__verb">Claim</span>
          )}
        </Link>
      ))}
      <Head title="Families waiting" next meta={`${fmtCount(b.families.total)} waiting${oldest ? ` · oldest ${dayHours(oldest.waitingSince, now)}` : ''}${sampleTail(b.real)}`} />
      {fam.map((f) => {
        const age = dayHours(f.waitingSince, now);
        return (
          <Link key={f.family} to="/family" className="far far--family">
            <span className="far__main">
              <span className="far__name">
                {f.family} <span className="far__room">{`re ${f.re}`}</span>
              </span>
              <span className="far__why">{f.what}</span>
            </span>
            <span className={`far__age${age.includes('d') ? ' op-ink--act' : ''}`}>{age}</span>
            <span className="far__verb">Reply</span>
          </Link>
        );
      })}
    </div>
  );
}

function NurseCol({ b, queues, now }: { b: BuildingOverview | null; queues: QueryState<OverviewQueues>; now: Date }) {
  const sampleTail = useSampleTail();
  if (!b) {
    return (
      <div className="fa-col">
        <WaitCount q={queues} which="nurse" title="Awaiting a nurse" />
      </div>
    );
  }
  const rows = b.nurse.rows;
  const oldest = rows.length ? Math.max(...rows.map((r) => now.getTime() - Date.parse(r.since))) : null;
  return (
    <div className="fa-col">
      <Head title="Awaiting a nurse" meta={`${fmtCount(b.nurse.total)} waiting${oldest !== null ? ` · oldest ${fmtAgeMinutes(oldest / 60000)}` : ''}${sampleTail(b.real)}`} />
      {rows.map((r) => {
        const min = (now.getTime() - Date.parse(r.since)) / 60000;
        return (
          <Link key={r.name + r.since} to="/residents" className="far far--nurse">
            <span className="far__main">
              <span className="far__name">{r.name}</span>
              <span className="far__why">{r.what}</span>
            </span>
            <span className={`far__age${min >= 60 ? ' op-ink--act' : ''}`}>{fmtAgeMinutes(min)}</span>
            <span className="far__verb">Act</span>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * The Caregivers on shift line. It counts only tasks assigned to these people by name
 * (building.ts): a task for "floor staff" or nobody is in Needs action's overdue count, not
 * here, so the line says whose tasks it counts.
 */
export function caregiverLoadLine(rows: { open: number; overdue: number }[]): string {
  const open = rows.reduce((t, r) => t + r.open, 0);
  const over = rows.reduce((t, r) => t + r.overdue, 0);
  return `assigned to them: ${open} open · ${over} overdue`;
}

function CaregiversAndMoney({ b, money }: { b: BuildingOverview | null; money: QueryState<CaptureSummary> }) {
  const sampleTail = useSampleTail();
  if (!b) {
    return (
      <div className="fa-col">
        <Head title="Caregivers on shift" />
        <Missing what={`a staffing route: who is on and their load (${BUILDING_ROUTE})`} />
        <Head title="Money on the table" next meta={money.data ? `this month (${money.data.period})` : undefined} />
        <QueryBlock q={money} rows={2}>
          {(d) => (
            <>
              {(
                [
                  ['Recoverable', d.recoverable],
                  ['Billable', d.measured],
                  ['Opportunity', d.opportunity],
                ] as const
              ).map(([k, f]) => (
                <Link key={k} to="/capture" className="fam fam--none" title={f.source}>
                  <span className="fam__name">{k}</span>
                  <span className="fam__amt">{fmtMoneyCompact(f.value === null ? null : f.value / 100)}</span>
                  <HonestyBadge flags={[{ real: d.real }, { basis: f.basis }]} figure={`Money ${k}`} className="ov-badge-mini" />
                </Link>
              ))}
            </>
          )}
        </QueryBlock>
      </div>
    );
  }
  const staff = b.caregivers.rows;
  return (
    <div className="fa-col">
      <Head title="Caregivers on shift" meta={caregiverLoadLine(staff) + sampleTail(b.real)} />
      {staff.map((r) => (
        <Link key={r.name + r.shift} to="/worklist" className={`far ${r.overdue > 0 ? 'far--over' : 'far--ok'}`}>
          <span className="far__main">
            <span className="far__name">
              {r.name} <span className="far__room">{`${r.unit ?? '–'} · ${r.shift}`}</span>
            </span>
            <span className="far__why far__why--muted">{r.overdue > 0 ? `${r.overdue} overdue` : 'on track'}</span>
          </span>
          <span className={`far__age far__age--big${r.overdue > 0 ? ' op-ink--act' : ' op-ink--none'}`}>{String(r.open)}</span>
          <span className="far__verb">Reassign</span>
        </Link>
      ))}
      <Head title="Money on the table" next meta={`soonest window first${sampleTail(b.real)}`} />
      {b.money.rows.map((m) => (
        <Link key={m.name + m.code} to="/finance" className={`fam fam--${m.closesInDays <= 10 ? 'act' : m.closesInDays <= 16 ? 'watch' : 'none'}`}>
          <span className="fam__name">{m.name}</span>
          <span className="fam__prog">{m.code}</span>
          <span className="fam__amt">{`$${Math.round(m.cents / 100)}`}</span>
          <span className={`fam__days ${m.closesInDays <= 10 ? 'op-ink--act' : m.closesInDays <= 16 ? 'op-ink--watch' : 'op-ink--none'}`}>{`${m.closesInDays}d`}</span>
        </Link>
      ))}
    </div>
  );
}

// Occupancy, the design's faOcc: scale 72 to 94 (widened to fit the data) so movement
// shows; the US quartile lines come from the reference benchmarks (CMS catalog, with
// its date), never from literals. Without them, only the bars and the peer line draw.
const OCC_LO = 72;
const OCC_HI = 94;
type OccLine = { v: number; c: string; label: string; w: number; right?: boolean };
/** The US lines: top quartile, median and bottom quartile of the CMS catalog. */
function occLines(o: OccupancyReference | null | undefined): OccLine[] {
  if (!o) return [];
  const asOf = o.asOf ? ` (CMS, ${o.asOf})` : ' (CMS)';
  return [
    { v: o.p75, c: '#1F6B52', label: `${o.p75}% US top quartile${asOf}`, w: 700 },
    { v: o.median, c: '#54606C', label: `${o.median}% US median`, w: 600 },
    { v: o.p25, c: '#B3261E', label: `${o.p25}% US bottom quartile`, w: 700 },
  ];
}
/** Bars shade against the same quartiles; one neutral shade when there are none. */
const occColor = (v: number, o: OccupancyReference | null | undefined) =>
  !o ? 'rgba(47,138,111,.62)' : v >= o.p75 ? '#2F8A6F' : v >= o.median ? 'rgba(47,138,111,.62)' : v >= o.p25 ? 'rgba(47,138,111,.36)' : '#B3261E';

function Occupancy({ months, peer, real, us }: { months: { month: string; pct: number }[]; peer: number | null; real: boolean | undefined; us: OccupancyReference | null | undefined }) {
  const sampleTail = useSampleTail();
  const last = months[months.length - 1];
  const base = occLines(us);
  const lines = peer === null ? base : [...base, { v: peer, c: '#264C7E', label: `typical for peers ${peer}%`, w: 600, right: true }];
  const all = [...months.map((m) => m.pct), ...lines.map((l) => l.v)];
  const lo = Math.min(OCC_LO, Math.floor(Math.min(...all)) - 2);
  const hi = Math.max(OCC_HI, Math.ceil(Math.max(...all)) + 2);
  const occY = (v: number) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
  return (
    <div className="fa-col">
      <Head title="Occupancy · 12 months" meta={`${last ? `${last.pct}% in ${last.month}` : 'no months yet'}${peer !== null ? ` · typical for peers ${peer}%` : ''}${sampleTail(real)}`} />
      <div className="fao">
        <span className="fao__bars">
          {months.map((m) => (
            <Link key={m.month} to="/performance" title={`${m.month} · ${m.pct}% · open Trends`} className="fao__bar" style={{ height: `${occY(m.pct)}%`, background: occColor(m.pct, us) }} />
          ))}
        </span>
        {lines.map((l) => (
          <span key={l.label}>
            <span className="fao__line" style={{ bottom: `${occY(l.v)}%`, borderTop: `1px dashed ${l.c}` }} />
            <span className="fao__label" style={{ bottom: `${occY(l.v)}%`, color: l.c, fontWeight: l.w, ...(l.right ? { right: 0 } : { left: 0 }) }}>
              {l.label}
            </span>
          </span>
        ))}
      </div>
      <span className="fao__months">
        {months.map((m) => (
          <span key={m.month}>{m.month}</span>
        ))}
      </span>
    </div>
  );
}

function monthOf(asof: string): string {
  return new Date(`${asof.slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short' });
}

export function FacilityAdminOverview() {
  const build = useBuildLabels();
  const { session, client } = useSession();
  const sampleTail = useSampleTail();
  const demoBuilding = useDemoBuilding();
  const badges = useNavBadges();
  const [tab, setTab] = useState<'overview' | 'triage' | 'todo'>('overview');
  const [more, setMore] = useState(false);
  const orgId = session?.user.organizationId ?? null;
  // To do (V4): read on every tab, since its count sits on the tab.
  const todo = useApi<OverviewTodo>((c, signal) => overviewTodo(c, { signal }));
  const doneToday = useApi<TodoDone>((c, signal) => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    return overviewTodoDone(c, { query: { since: midnight.toISOString() }, signal });
  }, [], tab === 'todo');
  const todoN = todoCount(todo.data);
  const checklist = useApi<FacilityChecklist>((c, signal) => api.facilityChecklist(c, { params: { id: orgId! }, signal }), [orgId], !!orgId);
  const census = useApi<OverviewCensus>((c, signal) => api.overviewCensus(c, { query: { days: 7 }, signal }));
  const queues = useApi<OverviewQueues>((c, signal) => api.overviewQueues(c, { signal }));
  const worklist = useApi<OverviewWorklistResponse>((c, signal) => api.overviewWorklist(c, { signal }));
  const usBench = useReferenceBenchmarks(client);
  const pulse = useApi<PulseV3Facilities>((c, signal) => api.pulseV3Facilities(c, { signal }));
  const money = useApi<CaptureSummary>((c, signal) => api.captureSummary(c, { signal }));
  const buildingQ = useApi<BuildingOverview>((c, signal) => api.overviewBuilding(c, { signal }));
  const triage = useApi<OverviewTriage>((c, signal) => api.overviewTriage(c, { signal }));
  const [placed, setPlaced] = useState<Record<string, { userId: string; name: string }>>({});
  const unplaced = unplacedCount(triage.data, placed);
  const b = buildingQ.data;
  const now = new Date();
  const hello = useGreeting(now);
  const cs = census.data && census.data.scoped ? census.data : null;
  const residents = cs?.current;
  const where = session?.user.orgName ?? null;

  const readOnly = useReadOnly();
  const onPrefs = async (p: { dismissed?: boolean; collapsed?: boolean }) => {
    if (!orgId || readOnly) return;
    const r = await api.facilityChecklistPrefs(client, { params: { id: orgId }, body: p });
    checklist.setData((prev) => (prev ? { ...prev, dismissed: r.dismissed, collapsed: r.collapsed } : prev));
    window.dispatchEvent(new CustomEvent(CHECKLIST_PREFS_EVENT, { detail: { dismissed: r.dismissed, collapsed: r.collapsed } }));
  };
  // A test alert was acknowledged: the checklist step and the escalation link both change.
  const reloadChecklist = checklist.reload;
  const reloadPulse = pulse.reload;
  const onTested = useCallback(() => {
    reloadChecklist();
    reloadPulse();
  }, [reloadChecklist, reloadPulse]);
  const since = b?.since
    ? `Since ${new Date(b.since.at).toLocaleDateString('en-US', { weekday: 'short' })} ${new Date(b.since.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · ${b.since.items.join(' · ')}${sampleTail(b.real)}`
    : null;
  const occMonths = b
    ? b.occupancy.months
    : queues.data && queues.data.scoped
      ? queues.data.occupancy.map((o) => ({ month: monthOf(o.asof), pct: Math.round(o.occupancy * (o.occupancy <= 1 ? 100 : 1)) }))
      : [];

  return (
    <>
      {/* V9 (isPlatform for the Facility Admin, L5337-5436): what needs you today and the team
          memo first, then the checklist, the family check-ins and "is it working", then the
          building in a scroller of its own. */}
      <NeedsYouToday todo={todo} onTriage={() => setTab('triage')} onTodo={() => setTab('todo')} />
      <TeamMemoCard />
      {/* The design hides these on the To do tab (fsShow, pfFaShow: faSec !== 'todo'). */}
      {tab !== 'todo' && <SetupChecklist q={checklist} onPrefs={onPrefs} onTested={onTested} />}
      {tab !== 'todo' && <FamilyCheckinsBlock />}
      {tab !== 'todo' && <IsItWorking q={pulse} />}
      <div className="op ov--admin" data-testid="overview-admin">
      <div className="op-tabs">
        <div role="tablist" aria-label="Overview sections" className="op-tabs__list">
          <button role="tab" type="button" aria-selected={tab === 'overview'} className={`op-tab${tab === 'overview' ? ' op-tab--on' : ''}`} onClick={() => setTab('overview')}>
            Overview
          </button>
          <button role="tab" type="button" aria-selected={tab === 'triage'} className={`op-tab${tab === 'triage' ? ' op-tab--on' : ''}`} onClick={() => setTab('triage')}>
            Triage
            {!!unplaced && <span className="op-tab__count">{unplaced}</span>}
          </button>
          <button role="tab" type="button" aria-selected={tab === 'todo'} className={`op-tab${tab === 'todo' ? ' op-tab--on' : ''}`} onClick={() => setTab('todo')}>
            To do
            {!!todoN && <span className="op-tab__count">{todoN}</span>}
          </button>
        </div>
      </div>

      {tab === 'todo' ? (
        <BuildingTodo todo={todo} done={doneToday} tenantLabel={session ? roleChipText(session) : ''} />
      ) : tab === 'triage' ? (
        <AdminTriage q={triage} placed={placed} onPlaced={(id, owner) => setPlaced((p) => ({ ...p, [id]: owner }))} />
      ) : (
        <>
          <div className="op-lede fa-lede">
            <span className="op-lede__left">
              <h2 className="op-lede__greet">
                {`${hello}.`}
                {/* The building's name is a name: never translated. */}
                {where && <span translate="no">{` ${where}.`}</span>}
              </h2>
              <span className="op-lede__meta">
                {session ? roleChipText(session) : ''}
                {' · '}
                <span className="tnum">{fmtClock(now)}</span>
                {' · '}
                {/* real: false means a seeded demo building (sample-orgs.ts), not an unwired feed:
                    every figure below is computed from that building's own rows. */}
                {`last 14 days${b && !b.real && !demoBuilding && build ? ' · SAMPLE' : ''}`}
              </span>
              {since && <span className="fa-lede__since">{since}</span>}
            </span>
            <span className="op-lede__right">
              <span className="op-lede__row">
                <span className="op-lede__v">{residents && residents.value !== null ? fmtCount(residents.value) : '–'}</span>
                {/* The same building, the same badge: a seeded demo building reads SAMPLE here
                    too, not "Live" beside blocks that say SAMPLE. */}
                {residents && (cs?.real !== false && flagsFromMeasure(residents)?.basis === 'measured' ? <span className="op-live">Live</span> : <HonestyBadge flags={[{ real: cs?.real }, flagsFromMeasure(residents)]} figure="Residents in the building" className="ov-badge-mini" />)}
              </span>
              <span className="op-lede__k">residents in the building</span>
            </span>
          </div>

          {/* V9 (faMoreTog, L5985): the building numbers fold under one button; the work above leads. */}
          <div style={{ display: 'flex', paddingTop: 8, borderTop: '1px solid rgba(34,39,46,.08)', marginBottom: more ? 8 : 0 }}>
            <button
              type="button"
              aria-expanded={more}
              data-testid="building-numbers-toggle"
              onClick={() => setMore((m) => !m)}
              style={{ display: 'inline-flex', alignItems: 'center', minHeight: 36, padding: '0 12px', borderRadius: 8, fontFamily: 'inherit', fontSize: 16, fontWeight: 700, color: '#264C7E', background: 'transparent', border: '1px solid rgba(47,92,151,.35)', cursor: 'pointer' }}
            >
              {more ? 'Hide building numbers' : 'Show building numbers'}
            </button>
          </div>

          {more && (
          <>
          <Hero b={b} census={census} />

          {b ? (
            <BuildingNeeds b={b} onTriage={() => setTab('triage')} />
          ) : (
            <NeedsTiles
              tiles={tilesFromBadges(badges.data)}
              title="Needs action"
              sub="Your building today"
              loading={badges.loading}
              cols={6}
              badge={badges.data ? <HonestyBadge flags={[{ real: badges.data.real }]} figure="Needs action (menu labels)" className="ov-badge-mini" hide={['MEASURED']} /> : undefined}
            />
          )}

          <div className="fa-band fa-band--four">
            <AttentionCol b={b} worklist={worklist} now={now} />
            <EscalationsAndFamilies b={b} queues={queues} now={now} />
            <NurseCol b={b} queues={queues} now={now} />
            <CaregiversAndMoney b={b} money={money} />
          </div>

          <div className="fa-band fa-band--occ">
            {occMonths.length ? <Occupancy months={occMonths} peer={b?.occupancy.peerMedianPct ?? null} real={b?.real} us={usBench.data?.occupancy} /> : (
              <div className="fa-col">
                <Head title="Occupancy · 12 months" />
                {queues.loading && !queues.data ? <Skeleton rows={2} /> : <EmptyState compact title="No occupancy history yet" body="It fills from the CMS catalog once your building's CCN is linked." />}
              </div>
            )}
            <div className="fa-col">
              <Head title="Census & Atlas" meta={b ? `this week${sampleTail(b.real)}` : undefined} />
              {b ? (
                <>
                  <SmallFigures cells={b.census} />
                  <span className="faq__gap" />
                  <SmallFigures cells={b.companion} />
                </>
              ) : (
                <Missing what={`census flow and Atlas reach for the week (${BUILDING_ROUTE})`} />
              )}
            </div>
          </div>

          <div className="fa-band fa-band--two">
            <div className="fa-col">
              <Head title="Survey readiness" meta={b ? `CMS measures vs state${sampleTail(b.real)}` : undefined} />
              {b ? (
                <>
                  <span className="fas">
                    {b.survey.chips.map((c) => (
                      <Link key={c.k} to="/performance" title={`${c.s} · open Performance`} className={`fas__chip fas__chip--${c.tone}`}>
                        <b>{c.k}</b>
                        <span>{c.s}</span>
                      </Link>
                    ))}
                  </span>
                  <SmallFigures cells={b.survey.measures} />
                </>
              ) : (
                <Missing what={`CMS measures against state, per building (${BUILDING_ROUTE})`} />
              )}
            </div>
            <div className="fa-col">
              <Head title="Revenue" meta={b ? 'this month · from Finance' : undefined} />
              {b ? <SmallFigures cells={b.revenue} /> : <Missing what={`a building revenue route from Finance (${BUILDING_ROUTE})`} />}
            </div>
          </div>

          <div className="fa-band fa-band--one">
            <div className="fa-col">
              <Head title="This month against last" meta={b ? `same building${sampleTail(b.real)}` : undefined} />
              {b ? <SmallFigures cells={b.month} /> : <Missing what={`month against month, same building (${BUILDING_ROUTE})`} />}
            </div>
          </div>
          </>
          )}
        </>
      )}
    </div>
    </>
  );
}
